import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Edit as EditIcon, Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { get } from "../utils/networkUtils";

const ScoringRulesListPage = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTournaments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get("/admin/scoring-rules");

      if (response?.data?.tournaments) {
        setTournaments(response.data.tournaments);
      }
    } catch (err) {
      console.error("Failed to fetch tournaments:", err);
      setError("Failed to load tournaments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const handleConfigure = (tournamentId) => {
    navigate(`/dashboard/scoring-rules/${tournamentId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Scoring Rules | DreamTeam Admin</title>
      </Helmet>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Scoring Rules</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tournament</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rule Set</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tournaments.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.competition_name || "Unnamed"}</TableCell>
                    <TableCell>{t.season_label || "-"}</TableCell>
                    <TableCell>
                      {t.has_scoring_rules ? (
                        <Chip label="Configured" color="success" size="small" />
                      ) : (
                        <Chip
                          label="Not Configured"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>{t.rule_set_name || "-"}</TableCell>
                    <TableCell>{t.rule_set_version || "-"}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={
                          t.has_scoring_rules ? <EditIcon /> : <AddIcon />
                        }
                        onClick={() => handleConfigure(t.id)}
                      >
                        {t.has_scoring_rules ? "Edit" : "Configure"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {tournaments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No tournaments found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </>
  );
};

export default ScoringRulesListPage;
